import React, { useState } from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Button,
  SxProps,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useNavigate } from "react-router-dom";
import AppLogo from "../assets/AppLogo.png";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useAuth } from "../context/AuthContext";
import {
  AISUMMARYREPORT,
  EVENTMESSAGEDASHBOARD,
  MANDATORYMESSAGEDASHBOARD,
} from "../routes/routePaths";
import { colors } from "../utils/constants";

const Sidebar: React.FC = () => {
  const { filteredMenuItems, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const navigate = useNavigate();
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const currentPath = window.location.pathname;

  return (
    <Drawer variant="permanent" anchor="left" sx={drawerStyles(isCollapsed)}>
      <Box sx={logoBoxStyles()}>
        <Tooltip title={isCollapsed ? "Intelligence Department" : ""}>
          <img src={AppLogo} alt="App Logo" style={logoStyles(isCollapsed)} />
        </Tooltip>
        {!isCollapsed && (
          <>
            <Typography fontWeight={"bold"} fontSize={14} ml={1}>
              Intelligence Department
            </Typography>
          </>
        )}
      </Box>
      <Tooltip
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        placement="right"
      >
        <IconButton
          onClick={toggleSidebar}
          sx={toggleButtonStyles(isCollapsed)}
        >
          {isCollapsed ? (
            <ArrowForwardIos sx={collapseIcon} />
          ) : (
            <ArrowBackIos sx={collapseIcon} />
          )}
        </IconButton>
      </Tooltip>
      <Box sx={dividerStyles(isCollapsed)} />
      <List sx={menuListStyles(isCollapsed)}>
        {filteredMenuItems?.map(({ id, to, text, Icon, path }) => {
          const isSelected =
            path === ""
              ? currentPath === to ||
                currentPath === EVENTMESSAGEDASHBOARD ||
                currentPath === MANDATORYMESSAGEDASHBOARD ||
                currentPath === AISUMMARYREPORT
              : currentPath.includes(path);

          return (
            <ListItem
              component={Link}
              to={to}
              key={id}
              sx={listItemStyles(isCollapsed)}
            >
              <Tooltip title={isCollapsed ? text : ""} placement="right">
                <ListItemButton
                  disableRipple
                  selected={isSelected}
                  sx={listItemButtonStyles(isCollapsed)}
                >
                  <ListItemIcon sx={listItemIconStyles}>
                    {typeof Icon === "string" ? (
                      <img
                        src={Icon}
                        alt={text}
                        style={{
                          filter: isSelected
                            ? "brightness(0) invert(1)"
                            : "none",
                        }}
                      />
                    ) : (
                      <Icon />
                    )}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText primary={text} sx={listItemTextStyles} />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* To do for later */}
      {/* <Box sx={{ mt: 'auto' }}>
        <Box sx={dividerStyles(isCollapsed)} />
        <Tooltip title={isCollapsed ? 'Logout' : ''} placement='right'>
          <Button sx={logoutButtonStyles(isCollapsed)} onClick={handleLogout}>
            <LogoutIcon />
            {!isCollapsed && 'Logout'}
          </Button>
        </Tooltip>
      </Box> */}
    </Drawer>
  );
};

export default Sidebar;

const drawerStyles = (isCollapsed: boolean): SxProps => ({
  "& .MuiDrawer-paper": {
    width: isCollapsed ? 100 : 250,
    bgcolor: colors.white,
    height: "100%",
    position: "relative",
    transition: "width 0.3s ease",
    overflowX: "hidden",
  },
});

const logoBoxStyles = (): SxProps => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  height: 64,
  paddingLeft: 2,
  paddingTop: 1,
  paddingRight: 2,
});

const logoStyles = (isCollapsed: boolean): React.CSSProperties => ({
  height: isCollapsed ? "60px" : "50px",
  transition: "all 0.3s ease",
  cursor: isCollapsed ? "pointer" : "default",
  width: "auto",
  objectFit: "contain",
  maxWidth: isCollapsed ? "60px" : "100%",
});

const dividerStyles = (isCollapsed: boolean): SxProps => ({
  width: isCollapsed ? "60px" : "180px",
  height: "1px",
  bgcolor: "rgba(255, 255, 255, 0.2)",
  mx: "auto",
  mt: 1,
  mb: 1,
});

const menuListStyles = (isCollapsed: boolean): SxProps => ({
  mt: isCollapsed ? "1rem" : "5%",
  mr: "10%",
});

const listItemStyles = (isCollapsed: boolean): SxProps => ({
  "&.MuiListItem-root": {
    p: 0,
    mb: 1,
    justifyContent: isCollapsed ? "center" : "flex-start",
    width: "100%",
  },
});

const listItemButtonStyles = (isCollapsed: boolean): SxProps => ({
  position: "relative",
  "&.Mui-selected": {
    color: "#fff",
    "& .MuiListItemIcon-root": { color: "#fff" },
    borderBottomRightRadius: "30px",
    borderTopRightRadius: "30px",
    backgroundImage: `
      linear-gradient(to right, ${colors.indigoBlueColor}, ${colors.lightSlateBlueColor})
    `,
    backgroundSize: "100% 100%",
    backgroundRepeat: "no-repeat",

    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: "5px",
      borderBottomRightRadius: "30px",
      borderTopRightRadius: "0",
      background: `linear-gradient(to right, ${colors.brownYellowColor} 0%, ${colors.brownYellowColor} 30%, ${colors.scarletColor} 30%, ${colors.scarletColor} 60%, transparent 60%, transparent 100%)`,
    },
    "& .MuiListItemText-root": {
      color: colors.white,
    },

    "&:hover": {
      backgroundImage: `
        linear-gradient(to right, ${colors.indigoBlueColor}, ${colors.lightSlateBlueColor})
      `,
      "&::after": {
        background: `linear-gradient(to right, ${colors.brownYellowColor} 0%, ${colors.brownYellowColor} 30%, ${colors.scarletColor} 30%, ${colors.scarletColor} 60%, transparent 60%, transparent 100%)`,
      },
    },
  },

  gap: isCollapsed ? 0 : 2,
  justifyContent: "center",
  pl: isCollapsed ? 2 : 4,
  height: "58px",
  ":hover": {
    background: colors.brightGrayColor,
    borderBottomRightRadius: "30px",
    borderTopRightRadius: "30px",
  },
});

const listItemIconStyles: SxProps = {
  justifyContent: "center",
  minWidth: 0,
  width: "24px",
  height: "24px",
};

const listItemTextStyles: SxProps = {
  color: colors.black,
  ".Mui-selected &": {
    color: colors.white,
  },
};

const logoutButtonStyles = (isCollapsed: boolean): SxProps => ({
  mt: "auto",
  bgcolor: "transparent",
  textTransform: "none",
  gap: isCollapsed ? 0 : 2,
  justifyContent: isCollapsed ? "center" : "flex-start",
  width: "100%",
  color: "white",
  pl: isCollapsed ? 1 : 4,
  mb: 2,
});

const toggleButtonStyles = (isCollapsed: boolean): SxProps => ({
  position: "absolute",
  left: isCollapsed ? "65px" : "220px",
  top: "70px",
  transform: "translateY(-50%)",
  minWidth: 0,
  padding: "4px",
  bgcolor: "#234B89",
  color: "white",
  borderRadius: "4px",
  zIndex: 10,
  "&:hover": {
    bgcolor: "#1a3a6e",
  },
  transition: "all 0.2s ease",
});

const collapseIcon: SxProps = {
  width: "12px",
  height: "12px",
};
